import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

/**
 * Save a single field on a contact via PATCH /api/contacts/:id.
 * - Optimistically updates any ["contact", id] cache present
 * - Rolls back on failure
 * - Toast + invalidate on success (also invalidates company contact lists)
 */
export function useContactField(
  contactId: string | undefined,
  companyId?: string,
  extraInvalidateKeys?: unknown[][],
) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ field, value }: { field: string; value: unknown }) => {
      if (!contactId) throw new Error("Missing contactId");
      const res = await fetch(`/api/contacts/${contactId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ [field]: value }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed to save (HTTP ${res.status})`);
      }
      return res.json();
    },
    onMutate: async ({ field, value }) => {
      if (!contactId) return;
      await queryClient.cancelQueries({ queryKey: ["contact", contactId] });
      const previous = queryClient.getQueryData<Record<string, unknown>>(["contact", contactId]);
      if (previous) {
        queryClient.setQueryData(["contact", contactId], { ...previous, [field]: value });
      }
      return { previous };
    },
    onError: (err, _vars, context) => {
      if (context?.previous && contactId) {
        queryClient.setQueryData(["contact", contactId], context.previous);
      }
      toast.error(err instanceof Error ? err.message : "Failed to save");
    },
    onSuccess: () => {
      toast.success("Saved");
      // An email edit changes the summary's Last email and FTC contact.
      queryClient.invalidateQueries({ queryKey: ["record-summary"] });
    },
    onSettled: () => {
      if (contactId) {
        queryClient.invalidateQueries({ queryKey: ["contact", contactId] });
      }
      if (companyId) {
        queryClient.invalidateQueries({ queryKey: ["company", companyId] });
        queryClient.invalidateQueries({ queryKey: ["contacts", companyId] });
      }
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      if (extraInvalidateKeys) {
        for (const key of extraInvalidateKeys) {
          queryClient.invalidateQueries({ queryKey: key });
        }
      }
    },
  });

  const saveField = (field: string, value: unknown) => mutation.mutate({ field, value });
  return { saveField, saveFieldAsync: (field: string, value: unknown) => mutation.mutateAsync({ field, value }), isSaving: mutation.isPending };
}
